import { useCallback } from "react";
import { Article } from "../../models/responses/article";
import { Order } from "../../models/responses/order";
import { Supplier } from "../../models/responses/supplier";
import { BrandDiscount } from "../../models/responses/brandDiscount";
import {
  calculateFromPercentage,
  calculateStoreSalesPriceFromSalesPrice,
  calculateWorkshopPriceFromSalesPrice,
} from "../services/calculationsHelperService";

// ============================================
// TABLE HANDLERS (inline editing in tables)
// ============================================

interface UseArticleTableHandlersProps {
  orderData: Order;
  setOrderData: React.Dispatch<React.SetStateAction<Order>>;
  brandDiscount: BrandDiscount | null;
}

export interface ArticleTableHandlers {
  onQuantityChange: (rowNumber: number, value: number) => void;
  onDescriptionChange: (rowNumber: number, value: string) => void;
  onArticleNumberChange: (rowNumber: number, value: string) => void;
  onSalesPriceChange: (rowNumber: number, value: string) => void;
  onDiscountChange: (rowNumber: number, value: string) => void;
  onTimeChange: (rowNumber: number, value: number) => void;
  onExcludeChange: (rowNumber: number, value: boolean) => void;
  // Additional handlers for edit.tsx
  onPurchaseOrderNumberChange: (rowNumber: number, value: string) => void;
  onCommentChange: (rowNumber: number, value: string) => void;
  onPurchasePriceChange: (rowNumber: number, value: string) => void;
  onSupplierChange: (rowNumber: number, supplierId: string) => void;
  onArticleNumberChangeWithTemplateClear: (rowNumber: number, value: string) => void;
}

export const useArticleTableHandlers = ({
  orderData,
  setOrderData,
  brandDiscount,
}: UseArticleTableHandlersProps): ArticleTableHandlers => {
  
  const onQuantityChange = useCallback((rowNumber: number, newQuantity: number) => {
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, quantity: newQuantity }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  const onDescriptionChange = useCallback((rowNumber: number, newDescription: string) => {
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, description: newDescription }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  const onArticleNumberChange = useCallback((rowNumber: number, newArticleNumber: string) => {
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, articleNumber: newArticleNumber }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  const onSalesPriceChange = useCallback((rowNumber: number, newSalesPrice: string) => {
    let value = newSalesPrice.replace(",", ".");
    if (value.startsWith("0") && !value.includes(".")) {
      value = value.substring(1);
    }
    if (isNaN(parseFloat(value))) value = "0";

    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? {
              ...article,
              salesPrice: value,
              storeSalesPrice: brandDiscount 
                ? calculateStoreSalesPriceFromSalesPrice(value, brandDiscount).toString()
                : value,
              workshopSalesPrice: brandDiscount
                ? calculateWorkshopPriceFromSalesPrice(value, brandDiscount).toString()
                : value,
            }
          : article
      ) ?? null,
    }));
  }, [setOrderData, brandDiscount]);

  const onDiscountChange = useCallback((rowNumber: number, newDiscount: string) => {
    if (newDiscount === "") {
      setOrderData(prev => ({
        ...prev,
        articles: prev.articles?.map((article) =>
          article.rowNumber === rowNumber
            ? { ...article, discount: null }
            : article
        ) ?? null,
      }));
      return;
    }
    const num = Number(newDiscount);
    if (isNaN(num)) return;
    if (num < 0 || num > 100) return;

    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, discount: num }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  const onTimeChange = useCallback((rowNumber: number, newTime: number) => {
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, time: newTime }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  const onExcludeChange = useCallback((rowNumber: number, value: boolean) => {
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, excludeFromWarehousePicking: value }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  // Additional handlers for edit.tsx
  const onPurchaseOrderNumberChange = useCallback((rowNumber: number, value: string) => {
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, purchaseOrderNumber: value }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  const onCommentChange = useCallback((rowNumber: number, value: string) => {
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, comment: value }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  const onPurchasePriceChange = useCallback((rowNumber: number, newPurchasePrice: string) => {
    setOrderData(prev => {
      let value = newPurchasePrice.replace(",", ".");
      if (value.startsWith("0") && !value.includes(".")) {
        value = value.substring(1);
      }
      const article = prev.articles?.find((a) => a.rowNumber === rowNumber);
      if (value.includes("%") && article) {
        value = calculateFromPercentage(article.salesPrice, value).toString();
      }
      if (isNaN(parseFloat(value))) value = "0";

      return {
        ...prev,
        articles: prev.articles?.map((a) =>
          a.rowNumber === rowNumber
            ? { ...a, articleTemplateId: null, purchasePrice: value }
            : a
        ) ?? null,
      };
    });
  }, [setOrderData]);

  const onSupplierChange = useCallback((rowNumber: number, supplierId: string) => {
    const newSupplierId = supplierId === "" ? null : supplierId;
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, articleTemplateId: null, supplierId: newSupplierId }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  const onArticleNumberChangeWithTemplateClear = useCallback((rowNumber: number, newArticleNumber: string) => {
    setOrderData(prev => ({
      ...prev,
      articles: prev.articles?.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, articleNumber: newArticleNumber, articleTemplateId: null }
          : article
      ) ?? null,
    }));
  }, [setOrderData]);

  return {
    onQuantityChange,
    onDescriptionChange,
    onArticleNumberChange,
    onSalesPriceChange,
    onDiscountChange,
    onTimeChange,
    onExcludeChange,
    onPurchaseOrderNumberChange,
    onCommentChange,
    onPurchasePriceChange,
    onSupplierChange,
    onArticleNumberChangeWithTemplateClear,
  };
};

// ============================================
// MODAL HANDLERS (editing in article modal)
// ============================================

interface UseArticleModalHandlersProps {
  orderData: Order;
  setOrderData: React.Dispatch<React.SetStateAction<Order>>;
  modalArticle: Article;
  setModalArticle: React.Dispatch<React.SetStateAction<Article>>;
  setShowArticleModal: React.Dispatch<React.SetStateAction<boolean>>;
  brandDiscount: BrandDiscount | null;
  suppliers: Supplier[];
}

export interface ArticleModalHandlers {
  onArticleNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeliveredQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onExternalArticleCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDiscountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPurchaseOrderNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInternalCommentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCommentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPurchasePriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSalesPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSupplierChange: (supplierId: string | null) => void;
  onReviewedQuoteChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValidatedChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onRemove: () => void;
  onOpen: (article: Article) => void;
  onClose: () => void;
}

export const useArticleModalHandlers = ({
  orderData,
  setOrderData,
  modalArticle,
  setModalArticle,
  setShowArticleModal,
  brandDiscount,
  suppliers,
}: UseArticleModalHandlersProps): ArticleModalHandlers => {

  const onArticleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, articleNumber: e.target.value }));
  }, [setModalArticle]);

  const onQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, quantity: parseInt(e.target.value) }));
  }, [setModalArticle]);

  const onDeliveredQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, deliveredQuantity: parseInt(e.target.value) }));
  }, [setModalArticle]);

  const onExternalArticleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, externalArticleCode: e.target.value }));
  }, [setModalArticle]);

  const onDiscountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setModalArticle(prev => ({ ...prev, discount: null }));
      return;
    }
    const num = Number(value);
    if (isNaN(num)) return;
    if (num < 0 || num > 100) return;
    setModalArticle(prev => ({ ...prev, discount: num }));
  }, [setModalArticle]);

  const onDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, description: e.target.value }));
  }, [setModalArticle]);

  const onPurchaseOrderNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, purchaseOrderNumber: e.target.value }));
  }, [setModalArticle]);

  const onInternalCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, internalComment: e.target.value }));
  }, [setModalArticle]);

  const onCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, comment: e.target.value }));
  }, [setModalArticle]);

  const onPurchasePriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(",", ".");
    if (value.startsWith("0") && !value.includes(".")) {
      value = value.substring(1);
    }
    setModalArticle(prev => {
      if (value.includes("%")) {
        value = calculateFromPercentage(prev.salesPrice, value).toString();
      }
      if (isNaN(parseFloat(value))) value = "0";
      return { ...prev, purchasePrice: value };
    });
  }, [setModalArticle]);

  const onSalesPriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(",", ".");
    if (value.startsWith("0") && !value.includes(".")) {
      value = value.substring(1);
    }
    if (isNaN(parseFloat(value))) value = "0";
    setModalArticle(prev => ({
      ...prev,
      salesPrice: value,
      storeSalesPrice: brandDiscount
        ? calculateStoreSalesPriceFromSalesPrice(value, brandDiscount).toString()
        : value,
      workshopSalesPrice: brandDiscount
        ? calculateWorkshopPriceFromSalesPrice(value, brandDiscount).toString()
        : value,
    }));
  }, [setModalArticle, brandDiscount]);

  const onSupplierChange = useCallback((supplierId: string | null) => {
    const id = supplierId === "" ? null : supplierId;
    setModalArticle(prev => ({
      ...prev,
      supplierId: id,
      supplier: suppliers.find((s) => s.id === id) || null,
    }));
  }, [setModalArticle, suppliers]);

  const onReviewedQuoteChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, reviewedQuote: e.target.checked }));
  }, [setModalArticle]);

  const onValidatedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle(prev => ({ ...prev, verified: e.target.checked }));
  }, [setModalArticle]);

  const onSave = useCallback(() => {
    setOrderData(prev => {
      const savedArticles = prev.articles?.map<Article>((a) => {
        if (a.rowNumber === modalArticle.rowNumber) return modalArticle;
        return a;
      }) ?? null;
      return { ...prev, articles: savedArticles };
    });
    setShowArticleModal(false);
  }, [setOrderData, modalArticle, setShowArticleModal]);

  const onRemove = useCallback(() => {
    setOrderData(prev => {
      const keptArticles = prev.articles?.filter((a) => a.rowNumber !== modalArticle.rowNumber) ?? null;
      return { ...prev, articles: keptArticles };
    });
    setShowArticleModal(false);
  }, [setOrderData, modalArticle.rowNumber, setShowArticleModal]);

  const onOpen = useCallback((article: Article) => {
    setModalArticle(prev => ({ ...prev, ...article }));
    setShowArticleModal(true);
  }, [setModalArticle, setShowArticleModal]);

  const onClose = useCallback(() => {
    setShowArticleModal(false);
  }, [setShowArticleModal]);

  return {
    onArticleNumberChange,
    onQuantityChange,
    onDeliveredQuantityChange,
    onExternalArticleCodeChange,
    onDiscountChange,
    onDescriptionChange,
    onPurchaseOrderNumberChange,
    onInternalCommentChange,
    onCommentChange,
    onPurchasePriceChange,
    onSalesPriceChange,
    onSupplierChange,
    onReviewedQuoteChange,
    onValidatedChange,
    onSave,
    onRemove,
    onOpen,
    onClose,
  };
};
